import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faUpload, faLightbulb, faSave, faTriangleExclamation, faPalette, faSliders, faCheck, faMoon, faSun, faUser, faPlug, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

const API = "https://api18081113.queroapp.pl";

const COLORS = [
    { label: 'Niebieski (Domyślny)', value: '#0d6efd', bg: 'bg-primary' },
    { label: 'Czerwony', value: '#dc3545', bg: 'bg-danger' },
    { label: 'Zielony', value: '#198754', bg: 'bg-success' },
    { label: 'Fioletowy', value: '#6f42c1', bg: 'bg-purple' },
    { label: 'Pomarańczowy', value: '#fd7e14', bg: 'bg-orange' },
    { label: 'Ciemny', value: '#212529', bg: 'bg-dark' },
];

export default function AdminEventSettings({ onUpdate }) {
    const navigate = useNavigate();
    const secretId = localStorage.getItem("secretId");

    // UI State
    const [activeTab, setActiveTab] = useState('general'); // 'general' | 'branding'
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");
    const [error, setError] = useState("");
    const fileInputRef = useRef(null);

    // Integrations State
    const [clients, setClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [newClientApp, setNewClientApp] = useState("");
    const [newClientUri, setNewClientUri] = useState("");
    const [clientError, setClientError] = useState("");

    const fetchClients = async () => {
        if (!secretId) return;
        setLoadingClients(true);
        try {
            const res = await fetch(`${API}/oauth/my-clients`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ secretId })
            });
            const d = await res.json();
            if (d.success) setClients(d.clients || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingClients(false);
        }
    };

    const handleCreateClient = async (e) => {
        e.preventDefault();
        setClientError("");
        if (!newClientApp || !newClientUri) return setClientError("Wypełnij oba pola.");
        try {
            const res = await fetch(`${API}/oauth/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ secretId, app_name: newClientApp, redirect_uri: newClientUri })
            });
            const d = await res.json();
            if (d.error) setClientError(d.error);
            else {
                setNewClientApp("");
                setNewClientUri("");
                fetchClients(); // refresh list
                setMsg("Dodano nową integrację!");
                setTimeout(() => setMsg(""), 3000);
            }
        } catch (e) { console.error(e); setClientError("Błąd sieci."); }
    };

    const handleDeleteClient = async (clientId) => {
        if (!window.confirm("Czy na pewno chcesz trwale usunąć tę integrację?")) return;
        setClientError("");
        try {
            const res = await fetch(`${API}/oauth/delete-client`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ secretId, client_id: clientId })
            });
            const d = await res.json();
            if (d.error) setClientError(d.error);
            else {
                fetchClients(); // refresh list
                setMsg("Usunięto integrację.");
                setTimeout(() => setMsg(""), 3000);
            }
        } catch (e) { console.error(e); setClientError("Błąd sieci."); }
    };

    // Data State
    const [formData, setFormData] = useState({
        event_name: "",
        date: "",
        adress: "",
        organizator: "",
        password: "",
        confirmPassword: "",
        "event-img": "",
        themeColor: "",
        themeMode: "light" // 'light' | 'dark'
    });

    // Initial Fetch
    useEffect(() => {
        if (!secretId) return;
        fetch(`${API}/event-settings/${secretId}`)
            .then(r => r.json())
            .then(d => {
                if (d && !d.error) {
                    setFormData(prev => ({
                        ...prev,
                        event_name: d.event_name || "",
                        date: d.date || "",
                        adress: d.adress || "",
                        organizator: d.organizator || "",
                        "event-img": d["event-img"] || "",
                        themeColor: d.theme_color || "",
                        themeMode: d.theme_mode || "light"
                    }));
                }
            })
            .catch(e => console.error("Error fetching settings:", e));

        fetchClients();
    }, [secretId]);

    // Handlers
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleColorSelect = (color) => {
        setFormData(prev => ({ ...prev, themeColor: color }));
    };

    const handleThemeModeToggle = (mode) => {
        setFormData(prev => ({ ...prev, themeMode: mode }));
    };

    const handleUpdate = async () => {
        setMsg("");
        setError("");

        // Password validation: only check if both are not empty, OR if one is filled
        if ((formData.password || formData.confirmPassword) && formData.password !== formData.confirmPassword) {
            setError("Hasła nie są identyczne.");
            return;
        }

        setLoading(true);
        try {
            const payload = new FormData();
            payload.append("secred-id", secretId);
            payload.append("event_name", formData.event_name);
            payload.append("date", formData.date);
            payload.append("adress", formData.adress);
            payload.append("organizator", formData.organizator);
            payload.append("event-img", formData["event-img"]);
            payload.append("themeColor", formData.themeColor);
            payload.append("themeMode", formData.themeMode);

            if (formData.password) {
                payload.append("password", formData.password);
            }

            const res = await fetch(`${API}/update-event-settings`, {
                method: "POST",
                body: payload
            });
            const data = await res.json();

            if (res.ok) {
                setMsg("Zapisano zmiany!");
                setFormData(prev => ({ ...prev, password: "", confirmPassword: "" }));
                setTimeout(() => setMsg(""), 3000);
                if (onUpdate) onUpdate();
            } else {
                setError(data.error || "Wystąpił błąd podczas zapisu.");
            }
        } catch (e) {
            setError("Błąd sieci. Sprawdź połączenie.");
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadData = new FormData();
        uploadData.append("event-img", file);
        uploadData.append("secred-id", secretId);

        try {
            setLoading(true);
            const res = await fetch(`${API}/upload-event-image-base64`, {
                method: "POST",
                body: uploadData
            });
            const data = await res.json();

            if (data.status === "success" || data.success) {
                const filename = data.url || data.filename || data.file;
                if (filename) {
                    setFormData(prev => ({ ...prev, "event-img": filename }));
                    if (onUpdate) onUpdate();
                }
            } else {
                setError("Błąd przesyłania logo: " + (data.message || "Nieznany błąd"));
            }
        } catch (e) {
            console.error(e);
            setError("Błąd przesyłania pliku.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteEvent = async () => {
        if (!window.confirm("Czy na pewno chcesz usunąć to wydarzenie? TEJ OPERACJI NIE MOŻNA COFNĄĆ! Wszystkie dane (ankiety, pytania, goście) zostaną utracone.")) {
            return;
        }

        try {
            const res = await fetch(`${API}/delete-event`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ secretId })
            });

            if (res.ok) {
                alert("Wydarzenie zostało usunięte.");
                localStorage.clear();
                window.location.href = "/";
            } else {
                alert("Wystąpił błąd podczas usuwania.");
            }
        } catch (e) {
            console.error(e);
            alert("Błąd sieci.");
        }
    };

    const LogoPreview = () => {
        const logo = formData["event-img"];
        const logoUrl = logo ? (String(logo).startsWith("http") ? logo : `https://eventicon.queroapp.pl/${logo}`) : null;

        return (
            <div className="d-flex align-items-center gap-4">
                {logoUrl ? (
                    <div className="p-2 border rounded-3 bg-white shadow-sm" style={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={logoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    </div>
                ) : (
                    <div className="d-flex align-items-center justify-content-center border rounded-3 bg-light text-secondary" style={{ width: 80, height: 80 }}>
                        <FontAwesomeIcon icon={faLightbulb} className="fs-2 opacity-25" />
                    </div>
                )}

                {logo ? (
                    <button
                        onClick={() => setFormData(p => ({ ...p, "event-img": "" }))}
                        className="btn btn-danger rounded-pill px-4 fw-bold shadow-sm"
                    >
                        Usuń
                    </button>
                ) : (
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="btn btn-outline-primary rounded-pill px-4 fw-bold"
                    >
                        Wgraj logo
                    </button>
                )}
                <input
                    type="file"
                    hidden
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleLogoUpload}
                />
            </div>
        )
    };

    // --- MOBILE PREVIEW COMPONENT (Abstract) ---
    const MobilePreview = () => {
        let primaryColor = formData.themeColor || '#0d6efd';
        const isDark = formData.themeMode === 'dark';

        // RULE: In dark mode, do not use black accent, force white
        if (isDark && primaryColor === '#212529') {
            primaryColor = '#ffffff';
        }

        const logo = formData["event-img"];
        const logoUrl = logo ? (String(logo).startsWith("http") ? logo : `https://eventicon.queroapp.pl/${logo}`) : null;

        // Abstract Theme Vars
        const bgBody = isDark ? '#121212' : '#f0f2f5'; // Light gray for app background
        const bgSurface = isDark ? '#1e1e1e' : '#ffffff'; // White for card/topbar
        const borderColor = isDark ? '#333' : '#e9ecef';
        const placeholderColor = isDark ? '#333' : '#e9ecef';
        const textareaBg = isDark ? '#2d2d2d' : '#ffffff';

        return (
            <div className="d-flex justify-content-center align-items-center h-100 pt-4">
                <div className="phone-mockup" style={{
                    width: '200px', // Smaller width
                    height: '400px', // Smaller height
                    backgroundColor: bgBody,
                    borderRadius: '30px',
                    border: '8px solid #adb5bd', // GRAY FRAME (Silver)
                    position: 'relative',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    transition: 'background-color 0.3s'
                }}>
                    {/* Notch */}
                    <div style={{
                        position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                        width: '40%', height: '18px', backgroundColor: '#adb5bd',
                        borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px', zIndex: 10
                    }}></div>

                    {/* Content Container */}
                    <div className="d-flex flex-column h-100">

                        {/* DISTINCT TOPBAR */}
                        <div className="d-flex align-items-center justify-content-between px-3 pt-4 pb-2 border-bottom"
                            style={{
                                height: '60px',
                                borderColor: borderColor,
                                backgroundColor: bgSurface, // Matches real topbar
                                zIndex: 2
                            }}>

                            {/* Left: Logo + Name Bar */}
                            <div className="d-flex align-items-center gap-2">
                                {logoUrl ? (
                                    <img src={logoUrl} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: placeholderColor }}></div>
                                )}
                                <div style={{ width: 50, height: 6, borderRadius: 3, background: placeholderColor }}></div>
                            </div>

                            {/* Right: Two Circles */}
                            <div className="d-flex gap-1">
                                <div style={{ width: 20, height: 20, borderRadius: '50%', background: placeholderColor }}></div>
                                <div style={{ width: 20, height: 20, borderRadius: '50%', background: placeholderColor }}></div>
                            </div>
                        </div>

                        {/* Main Body */}
                        <div className="flex-grow-1 p-3 d-flex flex-column justify-content-center">

                            {/* Title Placeholders */}
                            <div className="d-flex flex-column align-items-center gap-2 mb-3 opacity-50">
                                <div style={{ width: '60%', height: 10, borderRadius: 5, background: placeholderColor }}></div>
                                <div style={{ width: '40%', height: 10, borderRadius: 5, background: placeholderColor }}></div>
                            </div>

                            {/* Card Area */}
                            <div className="w-100 rounded-4 p-3 d-flex flex-column"
                                style={{ backgroundColor: bgSurface, minHeight: '200px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>

                                {/* DISTINCT TEXTAREA */}
                                <div className="w-100 rounded-3 mb-3 flex-grow-1"
                                    style={{
                                        backgroundColor: textareaBg,
                                        border: `2px solid ${primaryColor}`,
                                        position: 'relative'
                                    }}>
                                    {/* Mock typing lines inside textarea */}
                                    <div style={{ position: 'absolute', top: 10, left: 10, width: '70%', height: 6, borderRadius: 3, background: isDark ? '#444' : '#e9ecef' }}></div>
                                </div>

                                {/* Button */}
                                <div style={{
                                    width: '100%',
                                    height: 32,
                                    borderRadius: 16,
                                    backgroundColor: primaryColor,
                                    boxShadow: `0 4px 10px -2px ${isDark && primaryColor === '#ffffff' ? 'rgba(255,255,255,0.3)' : primaryColor + '66'}`,
                                    transition: 'background-color 0.3s'
                                }}></div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid py-5 px-4 px-md-5" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <style>{`
                .ls-1 { letter-spacing: 1px; }
                .hover-lift:hover { transform: translateY(-2px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important; }
                .section-header { 
                    font-size: 0.85rem; 
                    font-weight: 700; 
                    text-transform: uppercase; 
                    letter-spacing: 1.5px; 
                    color: #adb5bd; 
                    margin-bottom: 1.5rem;
                }
                .form-control-modern {
                    background-color: #f8f9fa;
                    border: 1px solid #e9ecef;
                    padding: 0.8rem 1rem;
                    font-weight: 500;
                    color: #212529;
                }
                .form-control-modern:focus {
                    background-color: #fff;
                    border-color: #a7aeb8;
                    box-shadow: 0 0 0 3px rgba(0,0,0,0.05);
                }
                .nav-pills-custom .nav-link {
                    color: #6c757d;
                    background-color: transparent;
                    font-weight: 600;
                    border-radius: 30px;
                    padding: 0.75rem 1.5rem;
                    transition: all 0.3s;
                }
                .nav-pills-custom .nav-link.active {
                    color: #fff;
                    background-color: #212529;
                    box-shadow: 0 4px 10px rgba(0,0,0,0.15);
                }
                .color-option {
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 2px solid transparent;
                }
                .color-option:hover { transform: scale(1.1); }
                .color-option.selected {
                    border-color: #212529;
                    transform: scale(1.15);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
                }
                .mode-toggle-btn {
                    border: 2px solid #e9ecef;
                    border-radius: 12px;
                    padding: 1rem;
                    cursor: pointer;
                    transition: all 0.2s;
                    text-align: center;
                    width: 100%;
                }
                .mode-toggle-btn.active {
                    border-color: #212529;
                    background-color: #f8f9fa;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }
            `}</style>

            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-5 gap-4">
                <h2 className="fw-bold m-0 ps-2">Ustawienia wydarzenia</h2>

                {/* TABS Navigation */}
                <div className="nav nav-pills nav-pills-custom bg-light rounded-pill p-1 d-inline-flex">
                    <button
                        className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                        onClick={() => setActiveTab('general')}
                    >
                        <FontAwesomeIcon icon={faSliders} className="me-2" />
                        Ogólne
                    </button>
                    <button
                        className={`nav-link ${activeTab === 'branding' ? 'active' : ''}`}
                        onClick={() => setActiveTab('branding')}
                    >
                        <FontAwesomeIcon icon={faPalette} className="me-2" />
                        Identyfikacja wizualna
                    </button>
                    <button
                        className={`nav-link ${activeTab === 'integrations' ? 'active' : ''}`}
                        onClick={() => setActiveTab('integrations')}
                    >
                        <FontAwesomeIcon icon={faPlug} className="me-2" />
                        Integracje
                    </button>
                </div>
            </div>

            {/* TAB: GENERAL */}
            {activeTab === 'general' && (
                <div className="animate-fade-in">
                    {/* SECTION: BASIC INFO */}
                    <section className="mb-5 pb-4 border-bottom">
                        <div className="px-2">
                            <h6 className="section-header">Podstawowe informacje</h6>

                            <div className="row align-items-center mb-4">
                                <div className="col-md-4 col-lg-3">
                                    <label className="fw-bold text-dark mb-0">Nazwa wydarzenia</label>
                                    <div className="small text-muted mt-1">Widoczna dla wszystkich gości</div>
                                </div>
                                <div className="col-md-8 col-lg-9">
                                    <input
                                        name="event_name"
                                        className="form-control form-control-modern rounded-3"
                                        placeholder="Nazwa twojego eventu"
                                        value={formData.event_name}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="row align-items-center mb-4">
                                <div className="col-md-4 col-lg-3">
                                    <label className="fw-bold text-dark mb-0">Data wydarzenia</label>
                                </div>
                                <div className="col-md-8 col-lg-9">
                                    <input
                                        name="date"
                                        className="form-control form-control-modern rounded-3"
                                        placeholder="DD.MM.RRRR"
                                        value={formData.date}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="row align-items-center mb-4">
                                <div className="col-md-4 col-lg-3">
                                    <label className="fw-bold text-dark mb-0">Adres</label>
                                </div>
                                <div className="col-md-8 col-lg-9">
                                    <input
                                        name="adress"
                                        className="form-control form-control-modern rounded-3"
                                        placeholder="Ulica, Miasto"
                                        value={formData.adress}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="row align-items-center mb-4">
                                <div className="col-md-4 col-lg-3">
                                    <label className="fw-bold text-dark mb-0">Organizator</label>
                                </div>
                                <div className="col-md-8 col-lg-9">
                                    <input
                                        name="organizator"
                                        className="form-control form-control-modern rounded-3"
                                        placeholder="Nazwa organizatora"
                                        value={formData.organizator}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* SECTION: SECURITY */}
                    <section className="mb-5 pb-4 border-bottom">
                        <div className="px-2">
                            <h6 className="section-header">Bezpieczeństwo</h6>

                            <div className="alert alert-light border-0 bg-light rounded-4 p-4 mb-4">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="small fw-bold text-uppercase text-muted mb-2 ls-1">Nowe hasło</label>
                                        <input
                                            type="password"
                                            name="password"
                                            autoComplete="new-password"
                                            className="form-control border-white shadow-sm rounded-3"
                                            value={formData.password}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="small fw-bold text-uppercase text-muted mb-2 ls-1">Potwierdź hasło</label>
                                        <input
                                            type="password"
                                            name="confirmPassword"
                                            autoComplete="new-password"
                                            className="form-control border-white shadow-sm rounded-3"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* SECTION: DANGER ZONE */}
                    <section className="mt-5 pt-4">
                        <div className="px-2">
                            <h6 className="section-header text-danger">Strefa niebezpieczna</h6>

                            <div className="border border-danger border-opacity-25 rounded-4 p-4 bg-danger bg-opacity-10 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-4">
                                <div>
                                    <h6 className="fw-bold text-danger mb-2">Usuń wydarzenie</h6>
                                    <p className="small text-danger text-opacity-75 mb-0" style={{ maxWidth: 500 }}>
                                        Ta operacja jest nieodwracalna. Spowoduje trwałe usunięcie wydarzenia, wszystkich powiązanych ankiet, pytań i kont gości.
                                    </p>
                                </div>
                                <button
                                    onClick={handleDeleteEvent}
                                    className="btn btn-danger rounded-pill px-4 fw-bold shadow-sm"
                                >
                                    Usuń konto
                                </button>
                            </div>
                        </div>
                    </section>
                </div>
            )}

            {/* TAB: BRANDING */}
            {activeTab === 'branding' && (
                <div className="animate-fade-in row">
                    <div className="col-lg-7">
                        {/* SECTION: LOGO */}
                        <section className="mb-5 pb-4 border-bottom">
                            <div className="px-2">
                                <h6 className="section-header">Logo wydarzenia</h6>
                                <div className="row align-items-center">
                                    <div className="col-md-4">
                                        <label className="fw-bold text-dark mb-0">Wgraj logo</label>
                                        <div className="small text-muted mt-1">Wyświetlane w nagłówku</div>
                                    </div>
                                    <div className="col-md-8">
                                        <LogoPreview />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* SECTION: THEME MODE */}
                        <section className="mb-5 pb-4 border-bottom">
                            <div className="px-2">
                                <h6 className="section-header">Tryb wyglądu</h6>
                                <div className="row g-3">
                                    <div className="col-6 col-md-4">
                                        <div
                                            className={`mode-toggle-btn ${formData.themeMode === 'light' ? 'active' : ''}`}
                                            onClick={() => handleThemeModeToggle('light')}
                                        >
                                            <FontAwesomeIcon icon={faSun} className="mb-2 fs-4 text-warning" />
                                            <div className="fw-bold small">Jasny</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-4">
                                        <div
                                            className={`mode-toggle-btn ${formData.themeMode === 'dark' ? 'active' : ''}`}
                                            onClick={() => handleThemeModeToggle('dark')}
                                        >
                                            <FontAwesomeIcon icon={faMoon} className="mb-2 fs-4 text-secondary" />
                                            <div className="fw-bold small">Ciemny</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* SECTION: COLOR PALETTE */}
                        <section className="mb-5">
                            <div className="px-2">
                                <h6 className="section-header">Paleta kolorystyczna</h6>
                                <p className="text-secondary small mb-4" style={{ maxWidth: 600 }}>
                                    Wybierz wiodący kolor Twojego wydarzenia. Zobaczysz podgląd na żywo na makiecie obok.
                                </p>

                                <div className="d-flex flex-wrap gap-3">
                                    {COLORS.map((c) => {
                                        const isSelected = formData.themeColor === c.value || (!formData.themeColor && c.value === '#0d6efd');
                                        const isWhiteOption = formData.themeMode === 'dark' && c.value === '#212529';
                                        const displayColor = isWhiteOption ? '#ffffff' : c.value;
                                        const checkColor = isWhiteOption ? '#212529' : '#ffffff';

                                        return (
                                            <div
                                                key={c.value}
                                                className={`color-option tooltip-container ${isSelected ? 'selected' : ''}`}
                                                style={{
                                                    backgroundColor: displayColor,
                                                    border: isWhiteOption ? '1px solid #dee2e6' : `2px solid ${displayColor}`
                                                }}
                                                onClick={() => handleColorSelect(c.value)}
                                                title={c.label}
                                            >
                                                {isSelected && <FontAwesomeIcon icon={faCheck} style={{ color: checkColor }} />}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* RIGHT COLUMN: PREVIEW */}
                    <div className="col-lg-5 d-none d-lg-block">
                        <div className="sticky-top pt-2" style={{ top: '100px', zIndex: 1 }}>
                            <div className="text-center mb-3">
                                <span className="badge bg-light text-secondary border rounded-pill px-3 py-2">
                                    PODGLĄD NA ŻYWO
                                </span>
                            </div>
                            <MobilePreview />
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: INTEGRATIONS */}
            {activeTab === 'integrations' && (
                <div className="animate-fade-in row">
                    <div className="col-12">
                        <section className="mb-5 pb-4">
                            <div className="px-2">
                                <h6 className="small text-secondary fw-bold text-uppercase mb-3" style={{ letterSpacing: '1px' }}>Prywatne aplikacje OAuth i Webhooki</h6>
                                <p className="text-muted small mb-5">
                                    Twórz integracje z innymi usługami, by móc wymieniać dane bezpośrednio z platformą SayQ.
                                </p>

                                <div className="mb-5">
                                    <h6 className="fw-bold mb-3 text-dark">Zarejestruj nową aplikację / klienta API</h6>
                                    {clientError && <div className="alert alert-danger py-2 small">{clientError}</div>}
                                    <form onSubmit={handleCreateClient} className="row g-3 align-items-end">
                                        <div className="col-md-5">
                                            <label className="small fw-bold text-secondary mb-2">Nazwa Aplikacji</label>
                                            <input type="text" className="form-control" style={{ borderRadius: '0.4rem', border: '1px solid #dee2e6', padding: '0.6rem 1rem' }} placeholder="np. Mój system zewnętrzny" value={newClientApp} onChange={e => setNewClientApp(e.target.value)} />
                                        </div>
                                        <div className="col-md-5">
                                            <label className="small fw-bold text-secondary mb-2">Redirect URI (zostaw puste dla API)</label>
                                            <input type="text" className="form-control" style={{ borderRadius: '0.4rem', border: '1px solid #dee2e6', padding: '0.6rem 1rem' }} placeholder="https://..." value={newClientUri} onChange={e => setNewClientUri(e.target.value)} />
                                        </div>
                                        <div className="col-md-2">
                                            <button type="submit" className="btn btn-dark w-100 fw-bold" style={{ borderRadius: '0.4rem', padding: '0.6rem 1rem' }}>
                                                <FontAwesomeIcon icon={faPlus} className="me-2" /> Dodaj
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <h6 className="fw-bold mb-4 text-dark">Twoje Klucze i Integracje</h6>
                                {loadingClients ? (
                                    <div className="text-secondary small">Ładowanie...</div>
                                ) : clients.length > 0 ? (
                                    <div className="table-responsive bg-white border" style={{ borderRadius: '0.6rem' }}>
                                        <table className="table table-hover align-middle m-0">
                                            <thead className="border-bottom">
                                                <tr>
                                                    <th className="small text-secondary fw-bold text-uppercase py-3 ps-4" style={{ letterSpacing: '0.5px' }}>Aplikacja</th>
                                                    <th className="small text-secondary fw-bold text-uppercase py-3" style={{ letterSpacing: '0.5px' }}>Client ID</th>
                                                    <th className="small text-secondary fw-bold text-uppercase py-3" style={{ letterSpacing: '0.5px' }}>Client Secret</th>
                                                    <th className="small text-secondary fw-bold text-uppercase py-3" style={{ letterSpacing: '0.5px' }}>Redirect URI</th>
                                                    <th className="small text-secondary fw-bold text-uppercase py-3 pe-4 text-end" style={{ letterSpacing: '0.5px' }}>Akcje</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {clients.map(c => (
                                                    <tr key={c.client_id} className="border-bottom">
                                                        <td className="fw-bold fs-6 ps-4 py-3 text-dark">{c.app_name}</td>
                                                        <td className="py-3">
                                                            <code className="px-2 py-1 rounded border d-inline-block text-dark" style={{ fontSize: '0.85rem', cursor: 'pointer', backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }} onClick={() => navigator.clipboard.writeText(c.client_id)} title="Kliknij, aby skopiować">
                                                                {c.client_id}
                                                            </code>
                                                        </td>
                                                        <td className="py-3">
                                                            <code className="px-2 py-1 rounded border d-inline-block text-dark" style={{ fontSize: '0.85rem', cursor: 'pointer', backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }} onClick={() => navigator.clipboard.writeText(c.client_secret)} title="Kliknij, aby skopiować">
                                                                {c.client_secret}
                                                            </code>
                                                        </td>
                                                        <td className="small text-muted py-3" style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.redirect_uri}>
                                                            {c.redirect_uri}
                                                        </td>
                                                        <td className="text-end pe-4 py-3">
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                style={{ borderRadius: '0.4rem' }}
                                                                onClick={() => handleDeleteClient(c.client_id)}
                                                                title="Usuń integrację"
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="p-4 border text-center text-muted bg-white" style={{ borderRadius: '0.6rem' }}>
                                        <p className="m-0">Brak skonfigurowanych integracji.</p>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            )}

            {/* FLOATING ACTION BAR FOR SAVE (Visible on all tabs) */}
            <div className="fixed-bottom bg-white border-top shadow-lg py-3 px-4 d-flex justify-content-end align-items-center gap-3" style={{ zIndex: 100 }}>
                {error && <span className="text-danger fw-bold small">{error}</span>}
                {msg && (
                    <span className="text-success fw-bold small animate-fade-in d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faSave} /> {msg}
                    </span>
                )}
                <button
                    onClick={handleUpdate}
                    disabled={loading}
                    className="btn btn-dark rounded-pill px-5 py-3 fw-bold shadow-lg hover-lift"
                >
                    {loading ? "Zapisywanie..." : "Zapisz zmiany"}
                </button>
            </div>

            <div className="pb-5 mb-5"></div>
        </div>
    );
};
